import { Link } from 'react-router-dom';
import { buttonClass, Card, MetricCard } from '@ledgerloom/ui';
import { useApi, usePageTitle } from '../hooks';
import { hasSurface, isAdmin, useSession } from '../session';
import { ErrorBanner, initials, Time } from '../components/ui';
import { Face } from '../components/TeamFace';
import { label, memberHref, useTeam, type Lane, type Member } from './Team';
import { hrefFor, PlannerAdvice } from './Calendar';
import { MyOkrs } from './Okrs';
import { inboxItem, type Inbox } from './Approvals';
import { areaDone, areaPending, type SetupArea } from './Welcome';
import { Notices, useNotices } from '../components/Notices';

// Home: the team reporting in. A bold summary of what needs you and who is
// doing what, today's numbers, and recent activity: the outcomes the
// workspace's topics say are important (ADR 0033). Patterns:
// data-display/statistic (the gauges and tiles), data-display/card (the team
// strip), and social/activity-feed (recent activity).

interface RecentActivity { items: Array<{ topic: string; line: string; count: number; at: string; ref: { type: string; id: string } | null; by: string | null }> }

interface WaitingChip { id: string; title: string; agent_name: string | null; kind: 'approval' | 'exception' }

// Each tile counts today's finished proposals of one kind (GET /v1/stats) and
// opens the same list in Activity.
const TILES = [
  { key: 'leads', schema: 'lead.create', label: 'New leads', note: (a: number) => (a ? `${a} brought in by agents` : 'Leads created today') },
  { key: 'replies', schema: 'comms.email_send', label: 'Replies sent', note: (a: number) => (a ? `${a} drafted by agents` : 'Emails sent today') },
  { key: 'deals_moved', schema: 'deal.move_stage', label: 'Deals moved', note: (a: number) => (a ? `${a} proposed by agents` : 'Stage changes today') },
  { key: 'quotes_sent', schema: 'quote.mark_sent', label: 'Quotes sent', note: (a: number) => (a ? `${a} drafted by agents` : 'Quotes sent today') },
] as const;

interface DayNow {
  now: { headline: string; next_action: { title: string; why: string; ref: { type: string; id: string } } | null };
  counts: { meetings: number };
}

// One literal class per tile, so Tailwind sees each.
const TILE_BG = ['bg-[var(--tile-1)]', 'bg-[var(--tile-2)]', 'bg-[var(--tile-3)]', 'bg-[var(--tile-4)]'];
const SHORT: Record<Lane, string> = { attention: 'Needs attention', working: 'Working', trial: 'On trial', quiet: 'Idle or paused' };
const LANES = Object.keys(SHORT) as Lane[];
const STRIP = 14;

export function Home() {
  const { me, stats } = useSession();
  const team = useTeam(0);
  const activity = useApi<RecentActivity>('/v1/recent-activity');
  const inbox = useApi<Inbox>('/v1/inbox');
  const agenda = useApi<DayNow>('/v1/agenda');
  // The setup card is the owner's while the interview was offered to them and not declined; the areas say how far it is.
  const setup = useApi<{ areas: SetupArea[] }>(me.role === 'owner' && me.onboarding && me.onboarding !== 'cancelled' ? '/v1/onboarding' : null);
  const areas = setup.data?.areas;
  const notices = useNotices();
  usePageTitle('Home');

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const n = stats?.inbox ?? stats?.my_approvals;
  // Problems first, then proposals: an exception reads by its own line, a proposal by its topic.
  const items: WaitingChip[] = [...(inbox.data?.items ?? [])].sort((a, b) => (a.source === b.source ? 0 : a.source === 'exception' ? -1 : 1)).slice(0, 3)
    .map((i) => ({ id: i.id, title: i.source === 'exception' ? i.title : i.topic, agent_name: i.from.kind === 'person' ? null : i.from.name, kind: i.source }));

  // The team is the agents. Attention first, so the strip leads with whichever needs someone.
  const order: Record<string, number> = { attention: 0, working: 1, trial: 2, quiet: 3 };
  const members = (team.data?.members ?? []).filter((m) => m.kind === 'agent').sort((a, b) => order[a.lane]! - order[b.lane]!);
  const shown = members.slice(0, STRIP);

  return (
    <div className="stack home">
      <Notices notices={notices.data ?? []} onChanged={() => void notices.reload()} here="/" />
      {/* The app's own card, until the interviewer posts its notice: before the first interview, or one from before notices. */}
      {hasSurface(me, 'setup') && areas && areas.some(areaPending) && notices.data && !notices.data.some((n) => n.progress?.source === 'setup') && (
        <section aria-label="Finish setting up">
          <Card className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <span><strong>Workspace setup: {areas.filter(areaDone).length} of {areas.length} done.</strong> Your onboarding agent sets up the rest of {me.tenant_name} with you.</span>
            <Link className={buttonClass()} to={me.onboarding === 'offered' ? '/welcome' : '/welcome/setup'}>{me.onboarding === 'offered' ? 'Start setup' : 'Continue setup'}</Link>
          </Card>
        </section>
      )}
      <section className="hero" aria-labelledby="hero-h">
        <div className="hero-main">
          <p className="hero-kicker">{greeting}, {me.display_name.split(' ')[0]} · {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h1 id="hero-h">
            {n === undefined ? 'Your team today.' : n === 0 ? 'Nothing needs you.' : `${n} ${n === 1 ? 'thing needs' : 'things need'} you.`}
            {n !== undefined && <span className="hero-accent">{n === 0 ? ' Your team is on it.' : ' Everything else is moving.'}</span>}
          </h1>
          {!!items.length && (
            <ul className="hero-chips" aria-label="Waiting for you">
              {items.map((it) => (
                <li key={`${it.kind}-${it.id}`}>
                  <Link className="hero-chip" to={inboxItem(it.id)}>
                    <i className={it.kind === 'exception' ? 'stuck' : 'attn'} aria-hidden="true">{initials(it.agent_name ?? 'You')}</i>
                    {it.title}<span className="sr-only">{it.kind === 'exception' ? ', a question from' : ', proposed by'} {it.agent_name ?? 'a person'}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div><Link className={buttonClass({ variant: 'secondary', className: 'border-[var(--hero-text)] bg-[var(--hero-text)] text-[var(--hero-bg)] can-hover:hover:bg-[var(--hero-text-2)]' })} to="/desk/needs-you">See what needs you</Link></div>
        </div>

        <div className="hero-gauges" aria-busy={!team.data}>
          <h2 className="sr-only">Team by status</h2>
          {team.error ? <ErrorBanner error={team.error} /> : (
            <ul className="gauges" aria-label="Team by status">
              {LANES.map((l) => <li key={l}><Gauge lane={l} title={SHORT[l]} n={members.filter((m) => m.lane === l).length} total={members.length} /></li>)}
            </ul>
          )}
        </div>
      </section>

      <section aria-labelledby="day-h">
        <Card className="grid gap-2 px-5 py-4">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="day-h" className="home-h2">Your day</h2>
          <Link to="/calendar?view=day" className="small">Open your day plan</Link>
        </div>
        {agenda.error ? <ErrorBanner error={agenda.error} /> : !agenda.data ? <p className="muted small" style={{ margin: 0 }}>Planning your day…</p> : (
          <>
            <p style={{ margin: 0 }}>{agenda.data.now.headline}{agenda.data.counts.meetings ? ` · ${agenda.data.counts.meetings} ${agenda.data.counts.meetings === 1 ? 'meeting' : 'meetings'} today` : ''}</p>
            {agenda.data.now.next_action && (
              <p style={{ margin: 0 }}><strong>Next: </strong>
                {hrefFor(agenda.data.now.next_action.ref) ? <Link to={hrefFor(agenda.data.now.next_action.ref)!}>{agenda.data.now.next_action.title}</Link> : agenda.data.now.next_action.title}
                <span className="muted small"> · {agenda.data.now.next_action.why}</span>
              </p>
            )}
          </>
        )}
        {hasSurface(me, 'day_plan') && <PlannerAdvice />}
        </Card>
      </section>

      {hasSurface(me, 'my_okrs') && <MyOkrs />}

      <section aria-labelledby="today-h" className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="today-h" className="home-h2">Today so far</h2>
          {!isAdmin(me) && <Link className="small" to={`/team/people/${encodeURIComponent(me.user_id)}`}>Your work, and the agents working for you</Link>}
        </div>
        <div className="tiles" aria-busy={!stats}>
          {TILES.map((t, i) => {
            const v = stats?.today?.[t.key];
            return (
              <Link key={t.key} className="tile" to={`/activity?${new URLSearchParams({ schema: t.schema, today: 'true', done: 'true' })}`}>
                <MetricCard label={t.label} value={v ? v.n : null} hint={v ? t.note(v.by_agents) : undefined}
                  className={`h-full border-transparent ${TILE_BG[i]} [&_p]:text-[var(--tile-text-2)] [&_p:first-child]:font-semibold [&_p:first-child]:text-[var(--tile-text)] [&_p:nth-child(2)]:font-[family-name:var(--font-numeric)] [&_p:nth-child(2)]:text-[var(--tile-text)]`} />
              </Link>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="team-h" className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="team-h" className="home-h2">Your team now</h2>
          <Link to="/desk" className="small">Open the Agent Desk</Link>
        </div>
        <Card className="team-now px-5 py-4">
          {team.error ? <ErrorBanner error={team.error} /> : (
            <ul className="team-strip" aria-busy={!team.data}>
              {shown.map((m) => <li key={m.id}><StripFace m={m} /></li>)}
              {members.length > STRIP && <li><Link className="team-more" to="/desk">+{members.length - STRIP} more</Link></li>}
            </ul>
          )}
          <ul className="team-legend" aria-label="Ring colours">
            {LANES.map((l) => <li key={l}><b className={`lg-${l}`} aria-hidden="true" />{SHORT[l]}</li>)}
          </ul>
        </Card>
      </section>

      <section aria-labelledby="act-h" className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="act-h" className="home-h2">Recent activity</h2>
          {hasSurface(me, 'workspace_designer') && <Link to="/admin/business-model" className="small">Change what shows here</Link>}
        </div>
        <ErrorBanner error={activity.error} />
        {activity.data && !activity.data.items.length ? (
          <Card className="px-5 py-4 text-secondary">Nothing yet. Deals won or lost, quotes, new leads, emails, meetings, and team changes show here as they happen.</Card>
        ) : (
          <Card><ul className="activity-feed" aria-busy={!activity.data}>
            {(activity.data?.items ?? []).map((a, i) => {
              const href = a.ref ? hrefFor(a.ref) : null;
              return (
                <li key={`${i}:${a.topic}:${a.at}`}>
                  {href ? <Link to={href} className="activity-line">{a.line}</Link> : <span className="activity-line">{a.line}</span>}
                  <span className="faint small"><Time iso={a.at} />{a.by ? ` · ${a.by}` : ''}</span>
                </li>
              );
            })}
          </ul></Card>
        )}
      </section>
    </div>
  );
}

// A ring gauge: the share of the team in one lane, the count in the middle.
// Pattern: data-display/statistic drawn as a progress ring.
const R = 30;
const C = 2 * Math.PI * R;
function Gauge({ lane, title, n, total }: { lane: string; title: string; n: number; total: number }) {
  const frac = total ? n / total : 0;
  return (
    <Link className={`gauge gauge-${lane}`} to="/desk" aria-label={`${n} of ${total} ${title.toLowerCase()}`}>
      <svg viewBox="0 0 72 72" aria-hidden="true">
        <circle className="gauge-track" cx="36" cy="36" r={R} />
        <circle className="gauge-arc" cx="36" cy="36" r={R} strokeDasharray={`${frac * C} ${C}`} strokeLinecap={n ? 'round' : 'butt'} />
        <text x="36" y="36" dominantBaseline="central" textAnchor="middle">{total ? n : '–'}</text>
      </svg>
      <span>{title}</span>
    </Link>
  );
}

function StripFace({ m }: { m: Member }) {
  const href = memberHref(m);
  return (
    <Link className="who" to={href}>
      <Face kind={m.kind} name={m.name} template={m.template_id} tone={m.tone} size={52} />
      <span className="who-name">{m.name}</span>
      <span className="who-status">{label(m)}</span>
    </Link>
  );
}
